<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import StandardRolePermissionsAPI from 'dashboard/api/standardRolePermissions';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  CUSTOM_ROLE_PERMISSION_GROUPS,
  expandLegacyPermissions,
} from 'dashboard/constants/permissions';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const profiles = ref({ agent: [] });
const editingRole = ref('');
const selectedPermissions = ref([]);
const loading = ref(true);
const saving = ref(false);

const roleCards = computed(() => [
  {
    role: 'agent',
    icon: 'i-lucide-headset',
    title: t('CUSTOM_ROLE.STANDARD_ROLES.AGENT.TITLE'),
    description: t('CUSTOM_ROLE.STANDARD_ROLES.AGENT.DESCRIPTION'),
  },
]);

const editingTitle = computed(
  () =>
    roleCards.value.find(item => item.role === editingRole.value)?.title || ''
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await StandardRolePermissionsAPI.get();
    profiles.value = data.roles;
  } catch {
    useAlert(t('CUSTOM_ROLE.STANDARD_ROLES.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const edit = role => {
  editingRole.value = role;
  selectedPermissions.value = expandLegacyPermissions(
    profiles.value[role] || []
  );
};

const close = () => {
  editingRole.value = '';
  selectedPermissions.value = [];
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await StandardRolePermissionsAPI.update(
      editingRole.value,
      selectedPermissions.value
    );
    profiles.value = data.roles;
    useAlert(t('CUSTOM_ROLE.STANDARD_ROLES.SAVE_SUCCESS'));
    close();
    window.location.reload();
  } catch {
    useAlert(t('CUSTOM_ROLE.STANDARD_ROLES.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <section class="space-y-3">
    <div>
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('CUSTOM_ROLE.STANDARD_ROLES.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('CUSTOM_ROLE.STANDARD_ROLES.DESCRIPTION') }}
      </p>
    </div>
    <div class="grid gap-3">
      <article
        v-for="item in roleCards"
        :key="item.role"
        class="flex items-start gap-3 p-4 border rounded-xl border-n-weak"
      >
        <span class="mt-0.5 size-5 text-n-brand" :class="[item.icon]" />
        <div class="flex-1 min-w-0">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ item.title }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ item.description }}
          </p>
          <p class="mt-2 text-xs text-n-slate-10">
            {{
              $t('CUSTOM_ROLE.STANDARD_ROLES.PERMISSION_COUNT', {
                count: profiles[item.role]?.length || 0,
              })
            }}
          </p>
        </div>
        <Button
          :label="$t('CUSTOM_ROLE.STANDARD_ROLES.EDIT')"
          icon="i-lucide-pencil"
          color="slate"
          size="sm"
          :disabled="loading"
          @click="edit(item.role)"
        />
      </article>
    </div>
  </section>

  <woot-modal :show="!!editingRole" :on-close="close">
    <div class="flex flex-col max-h-[80vh]">
      <woot-modal-header
        :header-title="
          $t('CUSTOM_ROLE.STANDARD_ROLES.MODAL_TITLE', {
            role: editingTitle,
          })
        "
        :header-content="$t('CUSTOM_ROLE.STANDARD_ROLES.MODAL_DESCRIPTION')"
      />
      <div class="flex-1 pr-1 overflow-y-auto">
        <section
          v-for="group in CUSTOM_ROLE_PERMISSION_GROUPS"
          :key="group.key"
          class="mb-5 space-y-2"
        >
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ $t(`CUSTOM_ROLE.PERMISSION_GROUPS.${group.key.toUpperCase()}`) }}
          </h4>
          <label
            v-for="permission in group.permissions.filter(item =>
              AVAILABLE_CUSTOM_ROLE_PERMISSIONS.includes(item)
            )"
            :key="permission"
            class="flex items-start gap-3 p-3 m-0 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:bg-n-alpha-2 cursor-pointer"
          >
            <input
              v-model="selectedPermissions"
              type="checkbox"
              :value="permission"
              class="mt-1"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    `CUSTOM_ROLE.PERMISSION_DETAILS.${permission.toUpperCase()}.TITLE`
                  )
                }}
              </span>
              <span class="block mt-0.5 text-xs text-n-slate-10">
                {{
                  $t(
                    `CUSTOM_ROLE.PERMISSION_DETAILS.${permission.toUpperCase()}.DESCRIPTION`
                  )
                }}
              </span>
            </span>
          </label>
        </section>
      </div>
      <div class="flex justify-end gap-2 pt-4">
        <Button
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          color="slate"
          faded
          @click="close"
        />
        <Button
          :label="$t('CUSTOM_ROLE.STANDARD_ROLES.SAVE')"
          :is-loading="saving"
          @click="save"
        />
      </div>
    </div>
  </woot-modal>
</template>
